import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import classNames from 'classnames';
import { createRoot } from 'react-dom/client';
import { z } from 'zod';
import { nonNull, zodPhpArrayObject } from '../core/utils';
import {
	LocationTag,
	locationParser,
	locationTagParser,
} from '../store/location';
import './locations-list.scss';
import { FormattedMessage } from 'react-intl';
import { I18nProvider } from '../core/i18n';

const locationDataParser = z.intersection(
	locationParser,
	z.object({
		permaLink: z.string(),
		thumbnail: z.string().or(z.literal(false)),
		environments: z.array(locationTagParser),
		distance: locationTagParser.nullable(),
	}),
);
type LocationListData = z.infer<typeof locationDataParser>;

(() => {
	document.addEventListener('DOMContentLoaded', () => {
		const root = document.querySelector('.focus_resa_location_list');
		if (!root)
			throw new Error(
				'Unable to find node .focus_resa_location_list to render to',
			);

		try {
			const raw = JSON.parse(root.getAttribute('data-locations') ?? '[]');
			const locationsData = zodPhpArrayObject(locationDataParser).parse(
				raw,
			) as LocationListData[]; // WHY need cast ??

			createRoot(root).render(
				<React.StrictMode>
					<I18nProvider>
						<LocationList locations={locationsData} />
					</I18nProvider>
				</React.StrictMode>,
			);
		} catch (e) {
			console.error(e.issues, e.format());
		}
	});
})();

const LocationList = (props: { locations: LocationListData[] }) => {
	const envs = useMemo(() => {
		return props.locations
			.reduce((allTags, l) => {
				l.environments.forEach(
					(t) => !allTags.some((t2) => t2.slug == t.slug) && allTags.push(t),
				);
				return allTags;
			}, [] as LocationTag[])
			.sort((t1, t2) => (t2.slug > t1.slug ? -1 : 1));
	}, [props.locations]);
	const dists = useMemo(() => {
		return props.locations
			.reduce((allTags, l) => {
				if (l.distance && !allTags.some((t2) => t2.slug == l.distance?.slug))
					allTags.push(l.distance);
				return allTags;
			}, [] as LocationTag[])
			.sort((t1, t2) => (t2.slug > t1.slug ? -1 : 1));
	}, [props.locations]);

	const [filter, setFilter] = useState([] as LocationTag[]);
	const setSelected = useCallback(
		(t: LocationTag, b: boolean) => {
			if (b)
				setFilter(
					filter.some((t2) => t2.slug == t.slug) ? filter : [...filter, t],
				);
			else setFilter(filter.filter((t2) => t2.slug != t.slug));
		},
		[filter],
	);

	useEffect(() => {
		const h = new URLSearchParams(location.hash.substring(1));
		const hashEnvs = h.getAll('environment');
		const f = [];
		f.push(
			...hashEnvs.map((hf) => envs.find((t) => t.slug == hf)).filter(nonNull),
		);
		const hashDist = h.getAll('distance');
		f.push(
			...hashDist.map((hf) => dists.find((t) => t.slug == hf)).filter(nonNull),
		);
		setFilter(f);
	}, [envs, dists, setFilter]);
	const noFirst = useRef(0);
	useEffect(() => {
		if (++noFirst.current < 2) return;
		const parts = [] as string[];
		for (const f of filter) parts.push(`${f.taxonomy}=${f.slug}`);
		location.href = `#${parts.join('&')}`;
	}, [filter]);

	const isFiltered = (l: LocationListData) => {
		const fenvs = filter.filter((f) => f.taxonomy == 'environment');
		const fdist = filter.filter((f) => f.taxonomy == 'distance');
		return (
			(fenvs.length == 0 ||
				l.environments.some((e) => fenvs.some((t2) => t2.slug == e.slug))) &&
			(fdist.length == 0 || fdist.some((t2) => t2.slug == l.distance?.slug))
		);
	};

	return (
		<>
			<div className="filters-section">
				<FormattedMessage defaultMessage="Filtres:" id="4ZOfxI" />
				<div className="filters-container">
					<div className="filters">
						{envs.map((t) => (
							<TagButton
								key={t.slug}
								tag={t}
								selected={filter.some((t2) => t2.slug == t.slug)}
								setSelected={setSelected}
							/>
						))}
					</div>
					<div className="filters">
						{dists.map((t) => (
							<TagButton
								key={t.slug}
								tag={t}
								selected={filter.some((t2) => t2.slug == t.slug)}
								setSelected={setSelected}
							/>
						))}
					</div>
				</div>
			</div>
			<ul>
				{props.locations.map((l) => (
					<a key={l.ID} href={l.permaLink}>
						<li
							className={classNames({
								location: true,
								filtered: isFiltered(l),
							})}
						>
							{l.thumbnail && <img src={l.thumbnail} />}
							<p>{l.post_title}</p>
						</li>
					</a>
				))}
			</ul>
		</>
	);
};

const TagButton = (props: {
	tag: LocationTag;
	selected: boolean;
	setSelected: (tag: LocationTag, selected: boolean) => void;
}) => (
	<div
		className={classNames('location-tag', {
			selected: props.selected,
		})}
		onClick={() => props.setSelected(props.tag, !props.selected)}
	>
		{props.tag.name}
	</div>
);
